import { POST_REG_APPLICATION_STATUS_MAP, PostRegApplicationStatus } from './post-reg-application-status.enum';

export enum ApplicationStatus  {
    NEW = 'NEW',
    AWAITING_BIN = 'AWAITING_BIN',
    UNDER_ASSESSMENT = 'UNDER_ASSESSMENT',
    ASSIGNED = 'ASSIGNED',
    RETURNED = 'RETURNED',
    REGISTERED = 'REGISTERED',
    ASSIGNED_REASSIGNMENT = 'ASSIGNED_REASSIGNMENT',
    //u_r
    AWAITING_APPROVAL = 'AWAITING_APPROVAL',
    //exp
    REJECTED = 'REJECTED',
    UPDATE_REQUIRED = `UPDATE_REQUIRED`,
    EXPIRED = `EXPIRED`,

    // Post-registration EVENT statuses.
    IN_PROCESS = 'IN_PROCESS',
    ACCEPTED = 'ACCEPTED',
    APPROVED = 'APPROVED',
    UNDER_APPROVAL = 'UNDER_APPROVAL',
    ASSESSMENT_REPORT_REVIEW = 'ASSESSMENT_REPORT_REVIEW',
    CONDITIONS_REVIEW = 'CONDITIONS_REVIEW',
    WRITTEN_NOTICE_ON_SIGNING = 'WRITTEN_NOTICE_ON_SIGNING',
    AWAITING_REVIEWER_ASSIGNMENT = 'AWAITING_REVIEWER_ASSIGNMENT',
    AWAITING_ANOTHER_EVENT = 'AWAITING_ANOTHER_EVENT',
    AWAITING_PARENT_EVENT = 'AWAITING_PARENT_EVENT',
    /** Update Corporate Structure: MoJ is still checking someone in the event (BRD Back Office Events). */
    AWAITING_MOJ = 'AWAITING_MOJ',
    COMPLETED = 'COMPLETED',
}
 
/** Maps a workflow status to its ng-zorro tag colour. Unmapped statuses fall back to 'default'. */
export const STATUS_COLOR_MAP: Partial<Record<ApplicationStatus, string>> = {
    [ApplicationStatus.ASSIGNED]: 'blue',
    [ApplicationStatus.UNDER_ASSESSMENT]: 'orange',
    [ApplicationStatus.AWAITING_BIN]: 'pink',
    [ApplicationStatus.REGISTERED]: 'green',
};

export const APPLICATION_STATUS_MAP: Map<ApplicationStatus, string> = new Map([
    [ApplicationStatus.NEW, 'New'],
    [ApplicationStatus.ASSIGNED, 'Assigned'],
    [ApplicationStatus.UNDER_ASSESSMENT, 'Under assessment'],
    [ApplicationStatus.AWAITING_APPROVAL, 'Awaiting approval'],
    [ApplicationStatus.AWAITING_BIN, 'Awaiting BIN'],
    [ApplicationStatus.REGISTERED, 'Registered'],
    [ApplicationStatus.RETURNED, 'Returned'],
    [ApplicationStatus.REJECTED, 'Rejected'],
    [ApplicationStatus.UPDATE_REQUIRED, 'Update required'],
    [ApplicationStatus.EXPIRED, 'Expired'],
    [ApplicationStatus.ASSIGNED_REASSIGNMENT, 'Reassigned'],

    // Post-registration event statuses — see the enum for why they share this map.
    [ApplicationStatus.IN_PROCESS, 'In process'],
    [ApplicationStatus.ACCEPTED, 'Accepted'],
    [ApplicationStatus.APPROVED, 'Approved'],
    [ApplicationStatus.UNDER_APPROVAL, 'Under approval'],
    [ApplicationStatus.ASSESSMENT_REPORT_REVIEW, 'Assessment report review'],
    [ApplicationStatus.CONDITIONS_REVIEW, 'Conditions review'],
    [ApplicationStatus.WRITTEN_NOTICE_ON_SIGNING, 'Written notice on signing'],
    [ApplicationStatus.AWAITING_REVIEWER_ASSIGNMENT, 'Awaiting reviewer assignment'],
    [ApplicationStatus.AWAITING_ANOTHER_EVENT, 'Awaiting another event'],
    [ApplicationStatus.AWAITING_PARENT_EVENT, 'Awaiting parent event'],
    [ApplicationStatus.AWAITING_MOJ, 'Awaiting MoJ'],
    [ApplicationStatus.COMPLETED, 'Completed'],
])

/**
 * The application list mixes registration and post-registration rows, so a status may belong to
 * either enum. Falls back to the raw code for statuses missing from both maps.
 */
export function getStatusLabel(status: string): string {
    return APPLICATION_STATUS_MAP.get(status as ApplicationStatus)
        ?? POST_REG_APPLICATION_STATUS_MAP.get(status as PostRegApplicationStatus)
        ?? status;
}

/** Status labels shown to the PARTICIPANT_SUPPORT_VIEWER role. Statuses absent here fall back to getStatusLabel. */
export const PARTICIPANT_SUPPORT_STATUS_MAP: Map<ApplicationStatus, string> = new Map([
    [ApplicationStatus.ASSIGNED, 'Assigned to Case Officer'],
    [ApplicationStatus.UNDER_ASSESSMENT, 'Under Review'],
    [ApplicationStatus.AWAITING_APPROVAL, 'Awaiting Approval'],
    [ApplicationStatus.AWAITING_BIN, 'Awaiting BIN'],
    [ApplicationStatus.RETURNED, 'Returned for Update'],
    [ApplicationStatus.REGISTERED, 'Registered'],
    [ApplicationStatus.REJECTED, 'Rejected'],
    [ApplicationStatus.EXPIRED, 'Expired'],
])

/** The only statuses the PARTICIPANT_SUPPORT_VIEWER role may filter by, in the order the business requires. */
export const PARTICIPANT_SUPPORT_STATUS_FILTER_ORDER: ApplicationStatus[] = [
    ApplicationStatus.ASSIGNED,
    ApplicationStatus.UNDER_ASSESSMENT,
    ApplicationStatus.AWAITING_APPROVAL,
    ApplicationStatus.AWAITING_BIN,
    ApplicationStatus.RETURNED,
    ApplicationStatus.REGISTERED,
    ApplicationStatus.REJECTED,
    ApplicationStatus.EXPIRED,
]
